import { monotonicNow, visible } from './websocketLifecycle.js'

export class WebSocketResync {
    constructor(service) {
        this.service = service
        this.consumers = new Map()
        this.revision = 0
        this.status = 'stale'
        this.lastWarning = null
        this.failureReported = false
    }
    invalidate() {
        this.revision++
        clearTimeout(this.timer)
        this.setStatus('stale')
    }
    resetWarnings() {
        this.lastWarning = null
        this.failureReported = false
    }
    setStatus(status) {
        this.status = status
        this.service.emit('sync_state', status)
    }
    register(name, refresh) {
        const key = Symbol(name)
        this.consumers.set(key, { name, refresh })
        return () => this.consumers.delete(key)
    }
    async run(attempt = 0) {
        clearTimeout(this.timer)
        if (!visible()) { this.invalidate(); return }
        const revision = ++this.revision
        const generation = this.service.generation
        const current = () => revision === this.revision && generation === this.service.generation && this.service.ready
        this.setStatus('syncing')
        const outcomes = await Promise.all([...this.consumers].map(async ([key, entry]) => {
            const valid = () => current() && this.consumers.get(key) === entry
            let result
            try {
                result = await entry.refresh(valid)
            } catch { result = false }
            // Superseded responses and busy consumers need another snapshot, not an error log.
            return { name: entry.name, result: valid() ? result : true }
        }))
        if (!current()) return
        if (!visible()) { this.invalidate(); return }
        const failed = outcomes.filter(item => item.result === false)
        const deferred = outcomes.some(item => item.result === 'deferred')
        const success = !failed.length && !deferred
        this.setStatus(success ? 'current' : failed.length ? 'failed' : 'stale')
        if (success && this.failureReported) {
            this.service.diagnostics.write('ws_resync', 'snapshot_refresh', { sync_result: 'succeeded', attempts: attempt })
            this.failureReported = false
            this.lastWarning = null
        }
        if (failed.length && attempt === 2 && (this.lastWarning === null || monotonicNow() - this.lastWarning >= 300000)) {
            this.service.diagnostics.write('ws_resync', 'snapshot_refresh', {
                consumer: [...new Set(failed.map(item => item.name))].join(' ').slice(0, 128),
                sync_result: 'failed', attempts: attempt + 1,
            }, 'warn')
            this.lastWarning = monotonicNow()
            this.failureReported = true
        }
        if (!success && attempt < 2) this.timer = setTimeout(() => void this.run(attempt + 1), 5000)
    }
}
